<template>
  <div class="mt-0 d-flex flex-column p-0 bg-transparent">
    <datasets-top-controls :facets="facets" :getPage="getPage" :getLimit="getLimit" class="datasets-top-controls"
      ref="sortComponent" @update-sort-type="handleSortType" />
    <div class="container-fluid datasets content">
      <datasets-title />
      <div class="row">
        <datasets-facets v-if="useDatasetFacets && showFilter"
          class="col-md-3 col-12 mb-3 mb-md-0 px-0 d-none d-md-block" id="datasetFacets"
          :dataScope="dataScope" />
        <datasets-content :in-series-details-page="inDetailsPage" :dss-id="dssId"/>
      </div>
      <div class="row" v-if="getSortType !== 'mostLiked'">
        <div class="column col-12 col-md-9 offset-md-3">
          <pagination class="mt-3" :items-count="getDatasetsCount" :items-per-page="getLimit" :get-page="getPage"
            :get-page-count="getPageCount" @setPageLimit="setPageLimit" :show-export="true" />
        </div>
      </div>
      <div class="row" v-else>
        <div class="column col-12 col-md-9 offset-md-3">
          <pagination class="mt-3" :items-count="getMostLikedTotal" :items-per-page="getLimit" :get-page="mostLikedPage"
            :get-page-count="Math.ceil(getMostLikedTotal / getLimit)" :manual-pagination-mode="true"
            @page-changed="handleMostLikedPageChange" @setPageLimit="setPageLimit" :show-export="true" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { debounce, isArray, isNil } from "lodash-es";
import $ from "jquery";
import {
  DatasetsTopControls,
  fileTypes
} from "@piveau/piveau-hub-ui-modules";
import DatasetsFacets from "./datasetFacets/DatasetsFacets.vue";
import DatasetsTitle from "@/components/search/datasets/DatasetsTitle";
import DatasetsContent from "@/components/search/datasets/DatasetsContent";
import Pagination from "../Pagination";

export default {
  name: "Datasets",
  components: {
    DatasetsContent,
    DatasetsTitle,
    DatasetsTopControls,
    DatasetsFacets,
    Pagination,
  },
  props: {
    infiniteScrolling: {
      type: Boolean,
      default: false,
    },inDetailsPage: {
      type: Boolean,
      default: false,
      required: false
    },
    dssId: {
      type: String,
      default: null
    }
  },
  metaInfo() {
    return {
      titleTemplate() {
        return this.$t("message.datasets.meta.page.title");
      },
      meta: [
        {
          name: "description",
          vmid: "description",
          content: `${this.$t("message.datasets.meta.description")}`,
        },
        {
          name: "keywords",
          vmid: "keywords",
          content: `${this.$env.keywords} ${this.$t(
            "message.datasets.meta.description"
          )}}`,
        },
        { name: "robots", vmid: "robots", content: "index, follow" },
      ],
    };
  },
  data() {
    return {
      debouncedOnBottomScroll: debounce(this.onBottomScroll, 500),
      facetFields: [],
      useDatasetFacets: this.$env.content.datasets.facets.useDatasetFacets,
      currentSortType: "",
    };
  },
  computed: {
    ...mapGetters("datasets", [
      "getDatasetsCount",
      "getFacets",
      "getLimit",
      "getSort",
      "getPage",
      "getPageCount",
      "getDatasets",
      "getMinScoring",
      "getMaxScoring",
      "search", ["getSortType"],
    ]),
    ...mapGetters("search", ["getSortType", 'getMostLikedTotal', 'getSortTriggerId', 'getShowFilter']),
    showCatalogDetails() {
      return !isNil(this.$route.params.ctlg_id);
    },
    showFilter() {
      return this.getShowFilter;
    },
    /**
     * @description Returns the current page.
     * @returns {Number}
     *
     * @deprecated use getPage from datasets store instead
     */
    page() {
      return this.$route.query.page;
    },
    /**
     * @description Returns the active facets.
     * @returns {Object}
     */


    facets() {
      const facets = {};
      for (const rawField of this.facetFields) {
        const field = rawField === "geospatial" ? "source_type" : rawField;
        if (Object.prototype.hasOwnProperty.call(facets, field)) continue;
        let urlFacets;
        if (field === "catalog" && !isNil(this.$route.params.ctlg_id))
          urlFacets = [this.$route.params.ctlg_id];
        else urlFacets = this.$route.query[field];
        if (!urlFacets) urlFacets = [];
        else if (!Array.isArray(urlFacets)) urlFacets = [urlFacets];
        facets[field] = urlFacets;
      }
      return facets;
    },
    currentSearchQuery() {
      return this.$route.query.query;
    },
    dataScope() {
      // Only the legacy "dataScope" route key drives the country-injection
      // mechanism in the datasets store / datasetService (values: eu, io, countryData).
      // The corporate-body-classification facet is handled as a regular facet
      // (it is part of defaultFacetOrder) and must NOT be passed to setDataScope,
      // otherwise its values (CB_EU, CB_OTHER, _NATIONAL) get incorrectly injected
      // into the "country" facet. Keep this in sync with Combined.vue.
      if (!this.$route.query.dataScope) return null;
      if (
        isArray(this.$route.query.dataScope) &&
        this.$route.query.dataScope.length > 0
      )
        return this.$route.query.dataScope[0];
      if (
        isArray(this.$route.query.dataScope) &&
        this.$route.query.dataScope.length === 0
      )
        return null;
      return this.$route.query.dataScope;
    },
    mostLikedPage() {
      return this.$store.state.search.mostLikedPage;
    }
  },
  methods: {
    isNil,
    ...mapActions("datasets", [
      "loadDatasets",
      "loadAdditionalDatasets",
      "setPage",
      "addFacet",
      "setFacets",
      "setFacetOperator",
      "setFacetGroupOperator",
      "setDataServices",
      "setPageCount",
      "setLimit",
      "setSort",
      "setDataScope",
      "setDateType",
      "setMaxScoring"
    ]),
    initMaxScoring() {
      const maxScoring = parseInt(this.$route.query.maxScoring, 10);
      if (maxScoring > 0) this.setMaxScoring(maxScoring);
    },
    isArray,
    /**
     * @description Handler-function for the scroll event.
     */
    onScroll() {
      const items = this.$el.querySelectorAll(".dataset");
      const lastItem = items[items.length - 1];
      if (lastItem) {
        const lastItemPos = lastItem.getBoundingClientRect();
        if (lastItemPos.bottom - window.innerHeight <= 0) {
          this.debouncedOnBottomScroll();
        }
      }
    },
    /**
     * @description Handler-function when bottom of the page is reached.
     */
    onBottomScroll() {
      this.$nextTick(() => {
        this.$Progress.start();
        this.loadAdditionalDatasets()
          .then(() => {
            this.$Progress.finish();
          })
          .catch(() => {
            this.$Progress.fail();
          });
      });
    },
    initLimit() {
      const limit = parseInt(this.$route.query.limit, 10);
      if (limit > 0) this.setLimit(limit);
    },
    initSort() {
      const sort = this.$route.query.sort;
      if (sort) this.setSort(sort);
    },
    setPageLimit(value) {
      this.setLimit(value);
      if (this.getSortType === "mostLiked") {
        sessionStorage.setItem("mostLikedPage", 1);
        this.$store.commit("search/SET_MOST_LIKED_PAGE", 1);
        this.$store.commit("search/TRIGGER_SORT_RELOAD");
      } else {
        this.initDatasets();
      }
    },
    initDataScope() {
      this.setDataScope(this.dataScope);
    },
    /**
     * @description Determines the current page.
     */
    initPage() {
      const page = parseInt(this.$route.query.page, 10);
      if (page > 0) this.setPage(page);
      else this.setPage(1);
    },
    /**
     * @descritption Initialize the active facets by checking the route parameters
     */

    initFacets() {
      const fields = this.$env.content.datasets.facets.defaultFacetOrder;
      for (const rawField of fields) {
        const field = rawField === "geospatial" ? "source_type" : rawField;
        if (!this.facetFields.includes(field)) this.facetFields.push(field);
        if (field === "catalog" && !isNil(this.$route.params.ctlg_id)) {
          this.addFacet({ field, facet: this.$route.params.ctlg_id });
        } else if (
          !Object.prototype.hasOwnProperty.call(this.$route.query, [field])
        ) {
          this.$router
            .replace({
              query: Object.assign({}, this.$route.query, { [field]: [] }),
            })
            .catch((error) => {
              console.error(error);
            });
        } else {
          let routeFacets = this.$route.query[field];
          if (!Array.isArray(routeFacets)) routeFacets = [routeFacets];
          for (const facet of routeFacets) {
            // do not add duplicates!
            if (!this.getFacets[field]?.includes(facet)) {
              this.addFacet({ field, facet });
            }
          }
        }
      }
    },
    initFacetOperator() {
      // Always set facet operator to AND when in catalog details mode
      if (this.showCatalogDetails) this.setFacetOperator("AND");
      else {
        const op = this.$route.query.facetOperator;
        if (op === "AND" || op === "OR") this.setFacetOperator(op);
      }
    },
    initFacetGroupOperator() {
      // The facetGroupOperator should be the same as the facetOperator
      // Always set facet operator to AND when in catalog details mode
      if (this.showCatalogDetails) this.setFacetGroupOperator("AND");
      else {
        const op = this.$route.query.facetOperator;
        if (op === "AND" || op === "OR") this.setFacetGroupOperator(op);
      }
    },
    /**
     * @descritption Initialize the active data services facet by checking the route parameters
     */
    initDataServices() {
      const ds = this.$route.query.dataServices;
      if (ds === "true" || ds === "false") this.setDataServices(ds);
      else {
        this.setDataServices("false");
      }
    },
    initDatasets() {
      this.$nextTick(() => {
        this.loadDatasets({
          locale: this.$route.query.locale,
          fields: this.$route.query.fields,
          minDate: this.$route.query.minDate,
          maxDate: this.$route.query.maxDate,
          dateType: this.$route.query.dateType,
          maxScoring: this.$route.query.maxScoring
        })
          .then(() => {
            this.setPageCount(
              Math.ceil(this.getDatasetsCount / this.getLimit)
            );
            this.$Progress.finish();
            $('[data-toggle="tooltip"]').tooltip({
              container: "body",
            });
          })
          .catch(() => this.$Progress.fail())
          .finally(() => this.$root.$emit("contentLoaded"));
      });
    },
    initInfiniteScrolling() {
      if (this.infiniteScrolling)
        window.addEventListener("scroll", this.onScroll);
    },
    getFileTypeColor(format) {
      return fileTypes.getFileTypeColor(format);
    },
    handleSortType(sortType) {
      this.currentSortType = sortType;
    },
    handleMostLikedPageChange(page) {
      if (this.getSortType === "mostLiked") {
        sessionStorage.setItem("mostLikedPage", page);
        this.$store.commit("search/SET_MOST_LIKED_PAGE", page);
        this.$store.commit("search/TRIGGER_SORT_RELOAD");
      }
    }
  },
  watch: {
    /**
     * @description Watcher for active facets
     */
    // eslint-disable-next-line object-shorthand
    facets: {
      handler(facets) {
        this.setFacets(facets);
      },
      deep: true,
    },
    // eslint-disable-next-line object-shorthand
    page(pageStr) {
      const page = parseInt(pageStr, 10);
      if (page > 0) this.setPage(page);
      else this.setPage(1);
    },
    dataScope: {
      handler() {
        this.initDataScope();
      },
      deep: true,
    },
    // Watch for changes in route query parameters that affect search results
    '$route.query': {
      handler(newQuery, oldQuery) {
        const relevantParams = ['fields', 'query', 'minDate', 'maxDate', 'dateType', 'maxScoring', 'page', 'dataScope', 'hvdCategory', 'country'];
        const hasRelevantChange = relevantParams.some(param =>
          (newQuery[param] || [])?.toString() !== (oldQuery[param] || [])?.toString()
        );

        if (hasRelevantChange) {
          this.$nextTick(() => {
            this.initDatasets();
          });
        }
      },
      deep: true
    }
  },
  created() {
    this.$Progress.start();
    if (this.$route.query.clear === "true") {
      this.$nextTick(() => {
        this.$router.push('/datasets/advanced');
      });
    }
    this.initDataScope();
    this.initLimit();
    this.initPage();
    this.initSort();
    this.initFacetOperator();
    this.initMaxScoring();
    this.initFacetGroupOperator();
    this.initDataServices();
    this.initFacets();
    this.initDatasets();
    this.initInfiniteScrolling();
    setTimeout(() => {
      $(".facets-show-more").each(function () {
        $(this).click(function () {
          $("body").tooltip({ selector: '[data-toggle="tooltip"]' });
        });
      });
    }, 1500);
  },
  mounted() {
    // This is supposed to fix the browser issue (https://gitlab.fokus.fraunhofer.de/piveau/organisation/piveau-scrum-board/-/issues/2344)
    if (this.$route.query.refresh === "true") {
      this.$nextTick(() => {
        this.loadDatasets({
          locale: this.$route.query.locale,
          dateType: this.$route.query.dateType,
          maxScoring: this.$route.query.maxScoring
        })
          .then(() => {
            this.$router.push({
              query: { locale: this.$route.query.locale },
            });
          })
          .catch(() => {
            this.$Progress.fail();
          });
      });
    }
  },
  beforeDestroy() {
    $(".tooltip").remove();
    if (this.infiniteScrolling)
      window.removeEventListener("scroll", this.onScroll);
  },
};
</script>

<style lang="scss" scoped>
.dataset-info-box:hover {
  box-shadow: none !important;
  cursor: pointer;
}

#erdpInfobox {
  display: flex;
  padding: 1.25rem;

  .infoIcon {
    height: 20%;
    width: 60%;
    fill: #175baf;
  }

  border: 2px solid #175baf;
}

.ecl-message.ecl-message--info {
  //border-left: 4px solid #0xe47cb !important;
  border-radius: 4px 0 0 4px;
  border: none;
  background-color: #f3f6fc;
}

.alert-primary {
  color: #042648;
  background-color: #cddbe8;
  border-color: #baccdf;
}

.content {
  padding: 30px 30px 0 30px;
  // margin-top: 15px;
  margin-bottom: 15px;
  background-color: white;
}

.router-link-active {
  color: #175baf !important;
  border: none !important;
  border-bottom: 2px solid #175baf !important;
}

.router-link-inactive {
  color: rgba(0, 0, 0, 0.7);
  border: none !important;

  &:hover {
    color: #175baf;
  }
}

.material-icons.small-icon {
  font-size: 20px;
}

@media screen and (min-width: 768px) {
  #erdpInfobox {
    margin-left: 15px;
  }

  #datasetFacets {
    display: block;
  }
}

@media screen and (max-width: 767px) {

  .datasets {
    padding-top: 5px;
  }

}
</style>
